import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  Paper,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  LinearProgress,
  IconButton,
  Fade,
  Slide
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  Extension as ExtensionIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  FeaturedPlayList as FeaturesIcon
} from '@mui/icons-material';
import ReactSelect from 'react-select';
import useProject from '../../hooks/useProject';
import useNotification from '../../hooks/useNotification';

const WebsiteRequirementsWizard = ({ projectId, onComplete, onCancel }) => {
  const { updateProject } = useProject();
  const { showNotification } = useNotification();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    cms: '',
    languages: ['English'],
    deeplIntegration: false,
    features: {
      homePage: true,
      aboutPage: false,
      services: false,
      products: false,
      contactPage: false,
      newsletter: false,
      socialMedia: false
    },
    otherFeatures: [],
    designInfo: '',
    designFiles: [],
    imagePreference: 'ai-generated',
    imageInstructions: ''
  });

  const [newFeature, setNewFeature] = useState('');
  const [errors, setErrors] = useState({});

  // Step definitions
  const steps = [
    {
      title: 'Content management system',
      subtitle: 'Which CMS do you prefer for your website?',
      icon: <ExtensionIcon />,
      field: 'cms'
    },
    {
      title: 'Languages',
      subtitle: 'Which languages should be activated on your website?',
      icon: <LanguageIcon />,
      field: 'languages'
    },
    {
      title: 'Website features',
      subtitle: 'Which features does your website need to have?',
      icon: <FeaturesIcon />,
      field: 'features'
    },
    {
      title: 'Design information',
      subtitle: 'Provide design references or describe the style you want',
      icon: <PaletteIcon />,
      field: 'designInfo'
    },
    {
      title: 'Image preferences',
      subtitle: 'How would you like to handle images for your website?',
      icon: <ImageIcon />,
      field: 'imagePreference'
    },
    {
      title: 'Review & Activate',
      subtitle: 'Review your requirements and activate development tiles',
      icon: <CheckIcon />,
      field: 'review'
    }
  ];

  const cmsOptions = [
    { value: 'drupal', label: 'Drupal', description: 'Powerful and flexible CMS for complex websites' },
    { value: 'wordpress', label: 'WordPress', description: 'Popular CMS with extensive plugin ecosystem' },
    { value: 'joomla', label: 'Joomla', description: 'User-friendly CMS with built-in multilingual support' },
    { value: 'no-preference', label: 'I have no preference', description: 'Let AI recommend the best CMS for your needs' }
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian',
    'Portuguese', 'Dutch', 'Russian', 'Chinese', 'Japanese',
    'Arabic', 'Hindi', 'Korean', 'Polish', 'Turkish'
  ];

  const featureOptions = [
    { key: 'homePage', label: 'Home page', description: 'Main landing page with key information' },
    { key: 'aboutPage', label: 'About us page', description: 'Company or organization information' },
    { key: 'services', label: 'Services', description: 'Showcase your services or offerings' },
    { key: 'products', label: 'Products', description: 'Display and manage product catalog' },
    { key: 'contactPage', label: 'Contact us page', description: 'Contact form and information' },
    { key: 'newsletter', label: 'Newsletter subscription', description: 'Email signup and management' },
    { key: 'socialMedia', label: 'Social media integration', description: 'Connect social media accounts' }
  ];

  // Calculate progress
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Auto-save to project after each step
  useEffect(() => {
    if (projectId && currentStep > 0) {
      saveProgress();
    }
  }, [currentStep]);

  const saveProgress = async () => {
    try {
      await updateProject({
        projectId,
        projectData: {
          websiteRequirements: {
            ...formData,
            currentStep,
            completed: false
          }
        }
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    const currentField = steps[currentStep].field;

    switch (currentField) {
      case 'cms':
        if (!formData.cms) {
          newErrors.cms = 'Please select a CMS preference';
        }
        break;
      case 'languages':
        if (formData.languages.length === 0) {
          newErrors.languages = 'Please select at least one language';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const processRequirementsAndActivateTiles = () => {
    // Define tiles to activate based on website project requirements
    const tilesToActivate = [
      // Analysis tiles
      'project-info',
      'features',
      'user-stories',
      'user-roles',
      'user-permissions',

      // UI/UX tiles
      'wireframes',
      'designs',
      'design-guidelines',

      // Technology tiles
      'field-validations',
      'tech-stack',
      'architecture',
      'unit-testing',
      'security-guidelines',
      'dev-guidelines',
      'git-guidelines',
      'api-guidelines',

      // Quality tiles
      'functional-testing',
      'performance-testing',
      'security-testing',
      'automated-testing',

      // Infrastructure tiles
      'ci-cd-pipeline',
      'process-flows',

      // Final documentation
      'final-prd',
      'development'
    ];

    // Generate project understanding based on requirements
    const projectUnderstanding = generateProjectUnderstanding(formData);

    return { tilesToActivate, projectUnderstanding };
  };

  const generateProjectUnderstanding = (requirements) => {
    const { cms, languages, features, designInfo, imagePreference, deeplIntegration, otherFeatures } = requirements;

    // Build a comprehensive understanding of the project
    let understanding = `## Website Project Overview\n\n`;

    // CMS Understanding
    understanding += `### Content Management System\n`;
    if (cms === 'no-preference') {
      understanding += `The client has no CMS preference. Based on the requirements, we'll recommend the most suitable option.\n\n`;
    } else {
      understanding += `The client has selected **${cms.charAt(0).toUpperCase() + cms.slice(1)}** as their CMS platform.\n\n`;
    }

    // Language Requirements
    understanding += `### Language Support\n`;
    understanding += `The website needs to support the following languages: ${languages.join(', ')}.\n`;
    if (deeplIntegration) {
      understanding += `DeepL integration is required for automatic translation between languages.\n`;
    }
    understanding += `\n`;

    // Features Understanding
    understanding += `### Core Features\n`;
    const selectedFeatures = Object.entries(features)
      .filter(([key, value]) => value)
      .map(([key]) => {
        const feature = featureOptions.find(f => f.key === key);
        return feature ? feature.label : key;
      });

    if (selectedFeatures.length > 0) {
      understanding += `The website will include:\n`;
      selectedFeatures.forEach(feature => {
        understanding += `- ${feature}\n`;
      });
    }

    if (otherFeatures.length > 0) {
      understanding += `\nAdditional custom features requested:\n`;
      otherFeatures.forEach(feature => {
        understanding += `- ${feature}\n`;
      });
    }
    understanding += `\n`;

    // Design Requirements
    if (designInfo) {
      understanding += `### Design Requirements\n`;
      understanding += `${designInfo}\n\n`;
    }

    // Image Strategy
    understanding += `### Image Strategy\n`;
    switch (imagePreference) {
      case 'upload':
        understanding += `The client will provide their own images for the website.\n`;
        break;
      case 'ai-generated':
        understanding += `Images will be generated using AI based on the content requirements.\n`;
        break;
      case 'free-stock':
        understanding += `Royalty-free stock images will be sourced for the website.\n`;
        break;
    }

    return understanding;
  };

  const renderReviewContent = () => {
    const { tilesToActivate, projectUnderstanding } = processRequirementsAndActivateTiles();

    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'primary.50' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: '600 !important', fontSize: '18px' }}>
            Project understanding
          </Typography>
          <Box sx={{
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            whiteSpace: 'pre-wrap',
            // Custom scrollbar styles
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              marginTop: '5px',
              marginBottom: '5px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.4)',
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
          }}>
            {projectUnderstanding}
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: '600 !important', fontSize: '18px' }}>
            Development tiles to activate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Based on your requirements, we'll automatically activate {tilesToActivate.length} development tiles:
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: '600 !important', fontSize: '14px', mb: 1 }}>
                 Analysis ({tilesToActivate.filter(t => ['project-info', 'features', 'user-stories', 'user-roles', 'user-permissions'].includes(t)).length})
              </Typography>
              {['project-info', 'features', 'user-stories', 'user-roles', 'user-permissions'].map(tile => (
                <Chip key={tile} label={tile.replace(/-/g, ' ')} size="small" sx={{ m: 0.5 }} />
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: '600 !important', fontSize: '14px', mb: 1 }}>
                 UI/UX ({tilesToActivate.filter(t => ['wireframes', 'designs', 'design-guidelines'].includes(t)).length})
              </Typography>
              {['wireframes', 'designs', 'design-guidelines'].map(tile => (
                <Chip key={tile} label={tile.replace(/-/g, ' ')} size="small" sx={{ m: 0.5 }} />
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: '600 !important', fontSize: '14px', mb: 1 }}>
                 Technology ({tilesToActivate.filter(t => ['field-validations', 'tech-stack', 'architecture', 'unit-testing', 'security-guidelines', 'dev-guidelines', 'git-guidelines', 'api-guidelines'].includes(t)).length})
              </Typography>
              {['field-validations', 'tech-stack', 'architecture', 'unit-testing', 'security-guidelines', 'dev-guidelines', 'git-guidelines', 'api-guidelines'].map(tile => (
                <Chip key={tile} label={tile.replace(/-/g, ' ')} size="small" sx={{ m: 0.5 }} />
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: '600 !important', fontSize: '14px', mb: 1 }}>
                 Quality ({tilesToActivate.filter(t => ['functional-testing', 'performance-testing', 'security-testing', 'automated-testing'].includes(t)).length})
              </Typography>
              {['functional-testing', 'performance-testing', 'security-testing', 'automated-testing'].map(tile => (
                <Chip key={tile} label={tile.replace(/-/g, ' ')} size="small" sx={{ m: 0.5 }} />
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: '600 !important', fontSize: '14px', mb: 1 }}>
                 Infrastructure ({tilesToActivate.filter(t => ['ci-cd-pipeline', 'process-flows'].includes(t)).length})
              </Typography>
              {['ci-cd-pipeline', 'process-flows'].map(tile => (
                <Chip key={tile} label={tile.replace(/-/g, ' ')} size="small" sx={{ m: 0.5 }} />
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Process requirements and get tiles to activate
      const { tilesToActivate, projectUnderstanding } = processRequirementsAndActivateTiles();

      // Save requirements and activate tiles
      await updateProject({
        projectId,
        projectData: {
          websiteRequirements: {
            ...formData,
            completed: true,
            projectUnderstanding
          },
          tasks: tilesToActivate,
          projectUnderstanding
        }
      });

      showNotification({
        message: `Website requirements processed! ${tilesToActivate.length} development tiles activated.`,
        type: 'success'
      });

      if (onComplete) {
        onComplete(formData);
      }
    } catch (error) {
      console.error('Error saving requirements:', error);
      showNotification({
        message: `Error saving requirements: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step.field) {
      case 'cms':
        return (
          <FormControl component="fieldset" error={!!errors.cms} fullWidth>
            <RadioGroup
              value={formData.cms}
              onChange={(e) => setFormData({ ...formData, cms: e.target.value })}
            >
              {cmsOptions.map((option) => (
                <Paper
                  key={option.value}
                  sx={{
                    mb: 2,
                    p: 2,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: formData.cms === option.value ? 'primary.main' : 'transparent',
                    backgroundColor: formData.cms === option.value ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => setFormData({ ...formData, cms: option.value })}
                >
                  <FormControlLabel
                    value={option.value}
                    control={<Radio color="primary" />}
                    label={
                      <Box>
                        <Typography variant="h5" sx={{ mb: 0.5,fontSize: '16px' }}>
                          {option.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </Paper>
              ))}
            </RadioGroup>
            {errors.cms && <FormHelperText>{errors.cms}</FormHelperText>}
          </FormControl>
        );

      case 'languages':
        const languageSelectOptions = languageOptions.map(lang => ({
          value: lang,
          label: lang
        }));

        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
              Select languages
            </Typography>
            <ReactSelect
              className="basic-multi-select"
              classNamePrefix="select"
              isMulti
              value={formData.languages.map(lang => ({ value: lang, label: lang }))}
              onChange={(selectedOptions) => {
                const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                setFormData({ ...formData, languages: values });
              }}
              options={languageSelectOptions}
              placeholder="Select languages"
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderRadius: '4px',
                  minHeight: '45px',
                  borderColor: state.isFocused ? '#475969' : '#4759694d',
                  boxShadow: state.isFocused ? 'none !important' : 'none',
                  '&:hover': {
                    borderColor: '#475969'
                  }
                }),
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: "14px !important"
                }),
                multiValue: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px'
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "14px !important",
                  color: 'var(--color-blue, #475969)'
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                  color: 'var(--color-blue, #475969)',
                  fontSize: '14px'
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isSelected ? '#e3f2fd' : state.isFocused ? '#f5f5f5' : 'white',
                  color: 'var(--color-blue, #475969)',
                  fontSize: '14px',
                  '&:active': {
                    backgroundColor: '#e3f2fd'
                  }
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999
                }),
                menuPortal: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999
                })
              }}
              menuPortalTarget={document.body}
            />
            {errors.languages && <FormHelperText error>{errors.languages}</FormHelperText>}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.deeplIntegration}
                  onChange={(e) => setFormData({ ...formData, deeplIntegration: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Enable DeepL integration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically translate content between selected languages
                  </Typography>
                </Box>
              }
              sx={{ mt: 3, alignItems: 'flex-start' }}
            />
          </Box>
        );

      case 'features':
        return (
          <Box>
            <FormGroup>
              {featureOptions.map((feature) => (
                <Paper
                  key={feature.key}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: formData.features[feature.key] ? 'primary.50' : 'background.paper',
                    border: '1px solid',
                    borderColor: formData.features[feature.key] ? 'primary.main' : 'divider'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.features[feature.key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: {
                            ...formData.features,
                            [feature.key]: e.target.checked
                          }
                        })}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">{feature.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                  />
                </Paper>
              ))}
            </FormGroup>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Other features
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'stretch' }}>
                <TextField
                  fullWidth
                  placeholder="Add custom feature..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newFeature.trim()) {
                      e.preventDefault();
                      setFormData({
                        ...formData,
                        otherFeatures: [...formData.otherFeatures, newFeature.trim()]
                      });
                      setNewFeature('');
                    }
                  }}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '4px',
                      height: '45px', // Match button height
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& input': {
                        padding: '12px 14px',
                        fontSize: '14px',
                        color: '#475969', // Input text color
                        fontFamily: "'Sofia Pro', sans-serif",
                        '&::placeholder': {
                          color: '#a2abb3',
                          opacity: 0.7,
                          fontSize: '14px',
                          fontFamily: "'Sofia Pro', sans-serif"
                        }
                      }
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newFeature.trim()) {
                      setFormData({
                        ...formData,
                        otherFeatures: [...formData.otherFeatures, newFeature.trim()]
                      });
                      setNewFeature('');
                    }
                  }}
                  startIcon={<AddIcon />}
                  sx={{ 
                    height: '45px',
                    minWidth: '100px',
                    textTransform: 'none',
                    fontSize: '14px'
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {formData.otherFeatures.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    onDelete={() => {
                      setFormData({
                        ...formData,
                        otherFeatures: formData.otherFeatures.filter((_, i) => i !== index)
                      });
                    }}
                    sx={{
                      '& .MuiChip-deleteIcon': {
                        '&:hover': {
                          color: '#475969' // Primary button color
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'designInfo':
        return (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Describe your design preferences, provide references, or paste URLs to websites you like..."
              value={formData.designInfo}
              onChange={(e) => setFormData({ ...formData, designInfo: e.target.value })}
              sx={{ 
                mb: 3,
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                  borderRadius: '4px',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#94a3b8',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '& textarea': {
                    fontSize: '14px',
                    color: '#475969',
                    fontFamily: "'Sofia Pro', sans-serif",
                    '&::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                      fontFamily: "'Sofia Pro', sans-serif"
                    }
                  }
                },
              }}
            />

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{ py: 2 }}
            >
              Upload design files (optional)
              <input
                type="file"
                hidden
                multiple
                accept="image/*,.pdf,.sketch,.fig"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setFormData({ ...formData, designFiles: [...formData.designFiles, ...files] });
                }}
              />
            </Button>

            {formData.designFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {formData.designFiles.length} file(s) uploaded
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'imagePreference':
        return (
          <Box>
            <RadioGroup
              value={formData.imagePreference}
              onChange={(e) => setFormData({ ...formData, imagePreference: e.target.value })}
            >
              {[
                { value: 'upload', label: 'I will upload my own images', description: 'You have your own images ready' },
                { value: 'ai-generated', label: 'Use AI to generate images', description: 'AI will create custom images based on your content' },
                { value: 'free-stock', label: 'Search and use free stock images', description: 'Find royalty-free images from stock libraries' }
              ].map((option) => (
                <Paper
                  key={option.value}
                  sx={{
                    mb: 2,
                    p: 2,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: formData.imagePreference === option.value ? 'primary.main' : 'transparent',
                    backgroundColor: formData.imagePreference === option.value ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => setFormData({ ...formData, imagePreference: option.value })}
                >
                  <FormControlLabel
                    value={option.value}
                    control={<Radio color="primary" />}
                    label={
                      <Box>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                  />
                </Paper>
              ))}
            </RadioGroup>

            {(formData.imagePreference === 'ai-generated' || formData.imagePreference === 'free-stock') && (
              <Fade in timeout={300}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe the type of images you want..."
                  value={formData.imageInstructions}
                  onChange={(e) => setFormData({ ...formData, imageInstructions: e.target.value })}
                  sx={{ 
                    mt: 3,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& textarea': {
                        fontSize: '14px',
                        color: '#475969',
                        fontFamily: "'Sofia Pro', sans-serif",
                        '&::placeholder': {
                          color: '#a2abb3',
                          opacity: 0.7,
                          fontSize: '14px',
                          fontFamily: "'Sofia Pro', sans-serif"
                        }
                      }
                    },
                  }}
                />
              </Fade>
            )}
          </Box>
        );

      case 'review':
        return renderReviewContent();

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', height:'100%' }}>
      <Box sx={{
        height: '90%', 
        overflow: 'auto',
        pr: 3, // Add right padding for scrollbar space
        // Custom scrollbar styles
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}>
        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Step {currentStep + 1} of {steps.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>

        {/* Step Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'primary.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main'
            }}>
              {React.cloneElement(steps[currentStep].icon, { fontSize: 'large' })}
            </Box>
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: '600 !important' }}>
            {steps[currentStep].title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {steps[currentStep].subtitle}
          </Typography>
        </Box>

        {/* Step Content */}
        <Slide direction="left" in mountOnEnter unmountOnExit>
          <Box sx={{ mb: 4, minHeight: 300 }}>
            {renderStepContent()}
          </Box>
        </Slide>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={currentStep === 0 ? onCancel : handleBack}
          startIcon={<ArrowBackIcon />}
          disabled={isSubmitting}
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={currentStep === steps.length - 1 ? <CheckIcon /> : <ArrowForwardIcon />}
          disabled={isSubmitting}
        >
          {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default WebsiteRequirementsWizard;